/**
 * Configuration service to centralize access to environment variables
 */
export class ConfigService {
  /**
   * Gets the environment name
   * @returns The environment name (e.g., 'erlia-stg-edl')
   */
  static getEnvName(): string {
    return import.meta.env.VITE_ENV_NAME || 'development';
  }

  /**
   * Checks if the current environment is staging
   * @returns True if the environment is staging
   */
  static isStaging(): boolean {
    return this.getEnvName() === 'erlia-stg-edl';
  }

  /**
   * Checks if the current environment is production
   * @returns True if the environment is production
   */
  static isProduction(): boolean {
    return this.getEnvName() === 'erlia-prod-edl';
  }

  /**
   * Checks if the current environment is local
   * @returns True if the environment is local or development
   */
  static isLocal(): boolean {
    const env = this.getEnvName();
    return env === 'erlia-local-edl' || env === 'development';
  }

  /**
   * Checks if the current environment is not production
   */
  static isTestEnvironment(): boolean {
    return !this.isProduction();
  }

  /**
   * Badge label for non-production environments
   */
  static getEnvironmentBadge(): { label: string; shortLabel: string; variant: 'staging' | 'local' } | null {
    if (this.isProduction()) return null;
    if (this.isStaging()) {
      return { label: 'ENTORNO STAGING', shortLabel: 'STG', variant: 'staging' };
    }
    return { label: 'ENTORNO LOCAL', shortLabel: 'LOC', variant: 'local' };
  }

  /**
   * Gets the anticipation limit in hours
   * @returns Number of hours for the anticipation limit
   */
  static getLimitAnticipation(): number {
    return parseFloat(import.meta.env.VITE_LIMIT_ANTICIPATION || '24');
  }

  /**
   * Gets the minimum scheduling limit in minutes
   * @returns Number of minutes for the minimum scheduling limit
   */
  static getLimitSchedulingMin(): number {
    return parseFloat(import.meta.env.VITE_LIMIT_SCHEDULING_MIN || '0');
  }

  /**
   * Gets the maximum scheduling limit in minutes
   * @returns Number of minutes for the maximum scheduling limit
   */
  static getLimitSchedulingMax(): number {
    const value = parseFloat(import.meta.env.VITE_LIMIT_SCHEDULING_MAX || '10080');
    // Si el valor es 0, usar el valor por defecto (10080 minutos = 168 horas)
    return value === 0 ? 10080 : value;
  }

  /**
   * Gets the minimum date for anticipation
   * @returns Minimum date allowed for anticipation
   */
  static getMinAnticipationDate(): Date {
    const now = new Date();
    const limitHours = this.getLimitAnticipation();
    const minDate = new Date(now.getTime() + limitHours * 60 * 60 * 1000);
    minDate.setMinutes(0, 0, 0);
    return minDate;
  }

  /**
   * Gets the minimum date for scheduling
   * @returns Minimum date allowed for scheduling
   */
  static getMinSchedulingDate(): Date {
    const now = new Date();
    const limitMinutes = this.getLimitSchedulingMin();
    return new Date(now.getTime() + limitMinutes * 60 * 1000);
  }

  /**
   * Gets the maximum date for scheduling
   * @returns Maximum date allowed for scheduling
   */
  static getMaxSchedulingDate(): Date {
    const now = new Date();
    const limitMinutes = this.getLimitSchedulingMax();
    return new Date(now.getTime() + limitMinutes * 60 * 1000);
  }

  /**
   * Validates if a date is within the allowed range for anticipation
   * @param date Date to validate
   * @returns true if the date is valid
   */
  static isValidAnticipationDate(date: Date): boolean {
    const minDate = this.getMinAnticipationDate();
    return date >= minDate;
  }

  /**
   * Validates if a date is within the allowed range for scheduling
   * @param date Date to validate
   * @returns true if the date is valid
   */
  static isValidSchedulingDate(date: Date): boolean {
    const minDate = this.getMinSchedulingDate();
    const maxDate = this.getMaxSchedulingDate();
    return date >= minDate && date <= maxDate;
  }

  /**
   * Gets the error message for invalid anticipation date
   * @returns Formatted error message
   */
  static getAnticipationErrorMessage(): string {
    const limitHours = this.getLimitAnticipation();
    return `La fecha de ocurrencia debe ser al menos ${limitHours} horas después de la hora actual`;
  }

  /**
   * Gets the error message for invalid scheduling date
   * @returns Formatted error message
   */
  static getSchedulingErrorMessage(): string {
    const minMinutes = this.getLimitSchedulingMin();
    const maxMinutes = this.getLimitSchedulingMax();
    
    if (minMinutes === 0) {
      const maxHours = Math.floor(maxMinutes / 60);
      return `La fecha de agendamiento debe estar entre ahora y ${maxHours} horas en el futuro`;
    } else {
      const minText = this.formatTimeRange(minMinutes / 60); // Convert minutes to hours for display
      const maxText = this.formatTimeRange(maxMinutes / 60); // Convert minutes to hours for display
      return `La fecha de agendamiento debe estar entre ${minText} y ${maxText} en el futuro`;
    }
  }

  /**
   * Formats a time in hours to display in error messages
   * @param hours Hours (can include decimals)
   * @returns Formatted text
   */
  private static formatTimeRange(hours: number): string {
    if (hours < 1) {
      const minutes = Math.round(hours * 60);
      return `${minutes} minutos`;
    } else if (hours === Math.floor(hours)) {
      return `${hours} horas`;
    } else {
      const wholeHours = Math.floor(hours);
      const minutes = Math.round((hours - wholeHours) * 60);
      if (minutes === 0) {
        return `${wholeHours} horas`;
      } else {
        return `${wholeHours} horas y ${minutes} minutos`;
      }
    }
  }

  /**
   * Gets the scheduling range as an object
   * @returns Object with min and max in minutes
   */
  static getSchedulingRange(): { min: number; max: number } {
    return {
      min: this.getLimitSchedulingMin(),
      max: this.getLimitSchedulingMax()
    };
  }

  /**
   * Gets all available feature flags
   * @returns Object containing all feature flags
   */
  static getFeatureFlags(): Record<string, boolean> {
    const flags: Record<string, boolean> = {};

    // Get all environment variables that start with VITE_FEATURE_FLAG_
    Object.entries(import.meta.env).forEach(([key, value]) => {
      if (key.startsWith('VITE_FEATURE_FLAG_')) {
        // Convert VITE_FEATURE_FLAG_ALLPHENOMENON to allPhenomenon
        const flagName = key
          .replace('VITE_FEATURE_FLAG_', '')
          .toLowerCase()
          .replace(/([A-Z])/g, (match) => match.toLowerCase());
        
        // Convert string value to boolean
        flags[flagName] = value === 'true';
      }
    });

    return flags;
  }

  /**
   * Gets a specific feature flag value
   * @param flagName - The name of the feature flag (e.g., 'allPhenomenon')
   * @returns The boolean value of the feature flag, or false if not found
   */
  static getFeatureFlag(flagName: string): boolean {
    const flags = this.getFeatureFlags();
    return flags[flagName] || false;
  }

  /**
   * Checks if a feature flag is enabled
   * @param flagName - The name of the feature flag
   * @returns True if the feature flag is enabled, false otherwise
   */
  static isFeatureEnabled(flagName: string): boolean {
    return this.getFeatureFlag(flagName);
  }

  /**
   * Checks if tropical cyclones feature flag is enabled
   * @returns True if enabled
   */
  static isTropicalCyclonesEnabled(): boolean {
    return import.meta.env.VITE_FEATURE_FLAG_TROPICALCYCLONES === 'true';
  }

  /**
   * Checks if drill feature flag is enabled
   * @returns True if enabled
   */
  static isDrillEnabled(): boolean {
    return import.meta.env.VITE_FEATURE_FLAG_DRILL === 'true';
  }

  static isInteractiveSelectionEnabled(): boolean {
    return import.meta.env.VITE_FEATURE_FLAG_INTERACTIVESELECTION === 'true';
  }

  /**
   * Checks if center monitoring feature flag is enabled
   * @returns True if enabled
   */
  static isCenterMonitoringEnabled(): boolean {
    return import.meta.env.VITE_FEATURE_FLAG_CENTROMONITOREO === 'true';
  }

  /**
   * Gets DateTimePicker restrictions based on selected value and mode
   * @param selectedValue Current selected date value
   * @param mode 'anticipation' or 'scheduling'
   * @returns Object with date and time restrictions
   */
  static getDateTimePickerRestrictions(selectedValue: string | undefined, mode: 'anticipation' | 'scheduling' = 'anticipation') {
    const now = new Date();
    
    // Get limits (anticipation in hours, scheduling in minutes)
    const limitAnticipation = this.getLimitAnticipation() * 60; // Convert to minutes
    const limitSchedulingMin = this.getLimitSchedulingMin(); // Already in minutes
    const limitSchedulingMax = this.getLimitSchedulingMax(); // Already in minutes
    
    // Calculate minimum date for anticipation (current time + anticipation limit)
    const minAnticipationDate = new Date(now.getTime() + limitAnticipation * 60 * 1000);
    minAnticipationDate.setMinutes(0, 0, 0);
    
    // Calculate range for scheduling (from now + min to now + max)
    const minSchedulingDate = new Date(now.getTime() + limitSchedulingMin * 60 * 1000);
    const maxSchedulingDate = new Date(now.getTime() + limitSchedulingMax * 60 * 1000);
    
    const selectedDate = selectedValue ? new Date(selectedValue.replace(" ", "T")) : null;
    
    // Calculate time restrictions based on selected date and mode
    let minTime: Date;
    let maxTime: Date;
    
    if (selectedDate) {
      const isToday = selectedDate.toDateString() === now.toDateString();
      
      if (mode === 'anticipation') {
        const isMinAnticipationDay = selectedDate.toDateString() === minAnticipationDate.toDateString();
        
        if (isToday) {
          // If today is selected for anticipation, restrict to current time + anticipation limit
          const currentTimePlusAnticipation = new Date(now.getTime() + limitAnticipation * 60 * 1000);
          minTime = new Date(0, 0, 0, currentTimePlusAnticipation.getHours(), currentTimePlusAnticipation.getMinutes(), 0, 0);
        } else if (isMinAnticipationDay) {
          // If minimum anticipation day is selected
          minTime = new Date(0, 0, 0, minAnticipationDate.getHours(), minAnticipationDate.getMinutes(), 0, 0);
        } else {
          // For other days, allow full day
          minTime = new Date(0, 0, 0, 0, 0, 0, 0);
        }
      } else {
        // Scheduling mode
        const isMinSchedulingDay = selectedDate.toDateString() === minSchedulingDate.toDateString();
        
        if (isToday) {
          // If today is selected for scheduling, restrict to current time + minimum scheduling limit
          const currentTimePlusMin = new Date(now.getTime() + limitSchedulingMin * 60 * 1000);
          minTime = new Date(0, 0, 0, currentTimePlusMin.getHours(), currentTimePlusMin.getMinutes(), 0, 0);
        } else if (isMinSchedulingDay) {
          // If minimum scheduling day is selected
          minTime = new Date(0, 0, 0, minSchedulingDate.getHours(), minSchedulingDate.getMinutes(), 0, 0);
        } else {
          // For other days, allow full day
          minTime = new Date(0, 0, 0, 0, 0, 0, 0);
        }
      }
      maxTime = new Date(0, 0, 0, 23, 59, 59, 999);
    } else {
      // No date selected, but we need to apply restrictions for today
      if (mode === 'scheduling') {
        // For scheduling mode, apply current time + minimum limit for today
        const currentTimePlusMin = new Date(now.getTime() + limitSchedulingMin * 60 * 1000);
        minTime = new Date(0, 0, 0, currentTimePlusMin.getHours(), currentTimePlusMin.getMinutes(), 0, 0);
      } else {
        // For anticipation mode, apply current time + anticipation limit for today
        const currentTimePlusAnticipation = new Date(now.getTime() + limitAnticipation * 60 * 1000);
        minTime = new Date(0, 0, 0, currentTimePlusAnticipation.getHours(), currentTimePlusAnticipation.getMinutes(), 0, 0);
      }
      maxTime = new Date(0, 0, 0, 23, 59, 59, 999);
    }
    
    return { 
      minDate: minAnticipationDate, 
      minTime, 
      maxTime,
      minSchedulingDate,
      maxSchedulingDate 
    };
  }

  /**
   * Gets the Ghost CMS API URL
   * @returns The Ghost CMS API URL
   */
  static getGhostApiUrl(): string {
    return import.meta.env.VITE_GHOST_API_URL || 'https://jalisco-alerta.ghost.io';
  }

  /**
   * Gets the Ghost CMS API Key
   * @returns The Ghost CMS API Key
   */
  static getGhostApiKey(): string {
    return import.meta.env.VITE_GHOST_API_KEY || '';
  }

  /**
   * Checks if Ghost CMS is properly configured
   * @returns True if both URL and API key are configured
   */
  static isGhostConfigured(): boolean {
    return !!(this.getGhostApiUrl() && this.getGhostApiKey());
  }

  /**
   * Gets the application version
   * @returns The application version (e.g., '2.14.1')
   */
  static getAppVersion(): string {
    return import.meta.env.VITE_APP_VERSION || '0.0.0';
  }
} 